import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { trackEvent } from "../analytics/eventQueue.js";
import { apiClient } from "../api/client.js";
import type { ApiPricingOption } from "./pricingBook.js";

type SessionState =
  | "loading"
  | "idle"
  | "awaiting_payment"
  | "active"
  | "expiring_soon"
  | "grace_period";

interface SessionInfo {
  id: string;
  verification_code: number;
  verification_phrase: string;
  status: string;
  starts_at?: string;
  ends_at?: string;
}

interface SessionRate {
  amount_pence: number;
  duration_minutes: number;
  currency: string;
}

type PricingScope = "fleet" | "venue";

interface PlaySessionContextData {
  state: SessionState;
  stateSeq: number;
  session: SessionInfo | null;
  sessionRate: SessionRate | null;
  pricingOptions: ApiPricingOption[];
  pricingScope: PricingScope;
  hasPendingPayment: boolean;
  phoneOnPayPage: boolean;
  timeRemainingSeconds: number;
  isInGame: boolean;
  isFreePlay: boolean;
  freePlayEndsAt: string | null;
  setIsInGame: (inGame: boolean) => void;
  createSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

const PlaySessionContext = createContext<PlaySessionContextData>({
  state: "loading",
  stateSeq: 0,
  session: null,
  sessionRate: null,
  pricingOptions: [],
  pricingScope: "fleet",
  hasPendingPayment: false,
  phoneOnPayPage: false,
  timeRemainingSeconds: 0,
  isInGame: false,
  isFreePlay: false,
  freePlayEndsAt: null,
  setIsInGame: () => {},
  createSession: async () => {},
  endSession: async () => {},
});

export function usePlaySession() {
  return useContext(PlaySessionContext);
}

const POLL_INTERVAL_MS = 3000;
const EXPIRING_SOON_THRESHOLD_SECONDS = 120;
const GRACE_PERIOD_SECONDS = 300;

function hasPlayableTime(session: SessionInfo | null | undefined): boolean {
  if (session?.status === "activating") return true;
  if (session?.status !== "active" || !session.ends_at) return false;
  const endsAtMs = new Date(session.ends_at).getTime();
  return Number.isFinite(endsAtMs) && endsAtMs > Date.now();
}

export function PlaySessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>("loading");
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [sessionRate, setSessionRate] = useState<SessionRate | null>(null);
  const [pricingOptions, setPricingOptions] = useState<ApiPricingOption[]>([]);
  const [pricingScope, setPricingScope] = useState<PricingScope>("fleet");
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [phoneOnPayPage, setPhoneOnPayPage] = useState(false);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0);
  const [isInGame, setIsInGame] = useState(false);
  const [stateSeq, setStateSeq] = useState(0);
  const [graceTimeRemaining, setGraceTimeRemaining] = useState(GRACE_PERIOD_SECONDS);
  const [isFreePlay, setIsFreePlay] = useState(false);
  const [freePlayEndsAt, setFreePlayEndsAt] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const creatingRef = useRef(false);
  const ackedSessionRef = useRef<string | null>(null);

  const emitSessionEnded = useCallback((endedSession: SessionInfo, reason: string) => {
    const props: Record<string, unknown> = { reason };
    if (endedSession.starts_at) {
      props.duration_ms = Date.now() - new Date(endedSession.starts_at).getTime();
    }
    props.play_session_id = endedSession.id;
    trackEvent("session_ended", props);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Acknowledge an activating session: this tells the server the table has visibly
  // unlocked, which releases the capture. Retried on the next poll if the call fails.
  const acknowledgeActivation = useCallback((sessionId: string) => {
    if (ackedSessionRef.current === sessionId) return;
    ackedSessionRef.current = sessionId;
    apiClient["/api/v1/content/play-sessions/{id}/ack"]
      ["post"]({
        parameters: { id: sessionId },
        body: null,
      })
      .catch((err) => {
        console.error("Error acknowledging session activation:", err);
        ackedSessionRef.current = null;
      });
  }, []);

  const fetchCurrentState = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/content/play-sessions/current"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as {
          session?: SessionInfo;
          has_pending_payment: boolean;
          phone_on_pay_page?: boolean;
          session_rate: SessionRate;
          pricing_options?: ApiPricingOption[];
          pricing_scope?: PricingScope;
          is_free_play: boolean;
          free_play_ends_at?: string;
        };
        setSessionRate(body.session_rate);
        setPricingOptions(body.pricing_options ?? []);
        setPricingScope(body.pricing_scope ?? "fleet");
        setSession(body.session ?? null);
        setHasPendingPayment(body.has_pending_payment);
        setPhoneOnPayPage(body.phone_on_pay_page ?? false);
        setIsFreePlay(body.is_free_play);
        setFreePlayEndsAt(body.free_play_ends_at ?? null);
        return body;
      }
    } catch (err) {
      console.error("Error fetching play session state:", err);
    }
    return null;
  }, []);

  const createSession = useCallback(async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    try {
      const res = await apiClient["/api/v1/content/play-sessions"]["post"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200 || res.code === 201) {
        const created = res.body as SessionInfo;
        setSession(created);
        if (created.status === "active") {
          setState("active");
        } else {
          setState("awaiting_payment");
        }
        setStateSeq((s) => s + 1);
      } else {
        console.error("Failed to create play session:", res.code, res.body);
      }
    } catch (err) {
      console.error("Error creating play session:", err);
    } finally {
      creatingRef.current = false;
    }
  }, []);

  const endSession = useCallback(async () => {
    const endingSession = session;
    try {
      const res = await apiClient["/api/v1/content/play-sessions/end"]["post"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        // An activating session renders as unlocked, so ending it is just as
        // much a user-visible session end as ending an active one.
        if (endingSession?.status === "active" || endingSession?.status === "activating") {
          emitSessionEnded(endingSession, "user_ended");
        }
        setSession(null);
        setHasPendingPayment(false);
        setPhoneOnPayPage(false);
        stopTimer();
        setState("idle");
      }
    } catch (err) {
      console.error("Error ending play session:", err);
    }
  }, [stopTimer, session, emitSessionEnded]);

  // The ack asserts "this table is visibly unlocked", so it must not fire
  // before the unlocked UI has actually been committed — effects run after
  // commit, and the state guard ensures the unlocked screen is what rendered.
  // Polls refresh `session` every 3s, so a failed ack (which clears the
  // dedupe ref) is retried on the next pass.
  useEffect(() => {
    if (session?.status !== "activating") return;
    if (state !== "active" && state !== "expiring_soon") return;
    acknowledgeActivation(session.id);
  }, [session, state, acknowledgeActivation]);

  // Initial load
  useEffect(() => {
    fetchCurrentState().then((data) => {
      if (!data || !data.session) {
        setState("idle");
        return;
      }
      setState(hasPlayableTime(data.session) ? "active" : "awaiting_payment");
    });
  }, [fetchCurrentState]);

  // Poll when idle to detect externally-created sessions (user pays via QR)
  useEffect(() => {
    if (state !== "idle") return;

    pollRef.current = setInterval(async () => {
      const data = await fetchCurrentState();
      if (hasPlayableTime(data?.session)) {
        stopPolling();
        setState("active");
        setStateSeq((s) => s + 1);
      } else if (data?.session) {
        // An unpaid session exposes a payment entry point even before a phone
        // claims a checkout. Phone presence is a separate signal.
        stopPolling();
        setState("awaiting_payment");
        setStateSeq((s) => s + 1);
      }
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [state, fetchCurrentState, stopPolling]);

  // Poll when awaiting payment
  useEffect(() => {
    if (state !== "awaiting_payment") return;

    pollRef.current = setInterval(async () => {
      const data = await fetchCurrentState();
      if (hasPlayableTime(data?.session)) {
        stopPolling();
        setState("active");
      } else if (data && !data.session) {
        stopPolling();
        setState("idle");
      }
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [state, fetchCurrentState, stopPolling]);

  // Countdown timer when active
  useEffect(() => {
    if (state !== "active" && state !== "expiring_soon") return;
    if (!session?.ends_at) return;

    const updateRemaining = () => {
      const endsAt = new Date(session.ends_at!).getTime();
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setTimeRemainingSeconds(remaining);

      if (remaining <= 0) {
        stopTimer();
      } else if (remaining < EXPIRING_SOON_THRESHOLD_SECONDS && state === "active") {
        setState("expiring_soon");
      }
    };

    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);

    return () => stopTimer();
  }, [state, session, stopTimer]);

  // Poll during active/expiring_soon to pick up extensions, capture completion
  // (ends_at appearing on an activating session), or a failed capture (session gone)
  useEffect(() => {
    if (state !== "active" && state !== "expiring_soon") return;

    const interval = setInterval(async () => {
      const data = await fetchCurrentState();
      if (data && !data.session) {
        stopTimer();
        if (isInGame && session?.status === "active" && session.ends_at) {
          // The server expires the row when this poll lands on or just after ends_at.
          // Treat a previously-paid in-game session as normal clock expiry; otherwise
          // this poll can win the race against the local timer and tear the game down
          // before the Times Up / buy-more-time grace screen is presented.
          setTimeRemainingSeconds(0);
          setGraceTimeRemaining(GRACE_PERIOD_SECONDS);
          setState("grace_period");
        } else {
          // An activating session with no end time disappeared before capture
          // completed (capture failure / ack-timeout race).
          setState("idle");
        }
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [state, fetchCurrentState, stopTimer, isInGame, session]);

  // Handle session expiry (only once the session has a real end time — an
  // activating session has no ends_at until capture completes)
  useEffect(() => {
    if (state !== "expiring_soon" && state !== "active") return;
    if (!session?.ends_at) return;
    if (timeRemainingSeconds > 0) return;
    // `timeRemainingSeconds` is updated by a separate effect. When a new or extended
    // `ends_at` first arrives it can still be zero for this render; the wall clock is
    // authoritative and prevents a paid session from entering grace on that stale value.
    const endsAtMs = new Date(session.ends_at).getTime();
    if (Number.isFinite(endsAtMs) && endsAtMs > Date.now()) return;

    if (isInGame) {
      setState("grace_period");
      setGraceTimeRemaining(GRACE_PERIOD_SECONDS);
    } else {
      fetchCurrentState().then((data) => {
        if (hasPlayableTime(data?.session)) {
          setState("active");
        } else if (data?.has_pending_payment || data?.session?.status === "awaiting_payment") {
          setState("awaiting_payment");
        } else {
          if (session?.status === "active") {
            emitSessionEnded(session, "timer_expired");
          }
          setState("idle");
        }
      });
    }
  }, [state, timeRemainingSeconds, isInGame, fetchCurrentState, session, emitSessionEnded]);

  // Grace period countdown
  useEffect(() => {
    if (state !== "grace_period") return;

    const countdownInterval = setInterval(() => {
      setGraceTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Payments happen outside the kiosk, so grace must keep observing the backend.
    // An `activating` result moves public state to active so the table sends the ack
    // that releases capture. An expired-looking active session may be retained while
    // its extension checkout is live, so only resume once its end time is in the future.
    const paymentPollInterval = setInterval(async () => {
      const data = await fetchCurrentState();
      if (hasPlayableTime(data?.session)) {
        setState("active");
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(countdownInterval);
      clearInterval(paymentPollInterval);
    };
  }, [state, fetchCurrentState]);

  // End grace period when game ends or time runs out
  useEffect(() => {
    if (state !== "grace_period") return;
    if (!isInGame || graceTimeRemaining <= 0) {
      fetchCurrentState().then((data) => {
        if (hasPlayableTime(data?.session)) {
          setState("active");
        } else if (data?.has_pending_payment || data?.session?.status === "awaiting_payment") {
          setState("awaiting_payment");
        } else {
          if (session?.status === "active") {
            emitSessionEnded(session, "grace_period_ended");
          }
          setState("idle");
        }
      });
    }
  }, [state, isInGame, graceTimeRemaining, fetchCurrentState, session, emitSessionEnded]);

  return (
    <PlaySessionContext.Provider
      value={{
        state,
        stateSeq,
        session,
        sessionRate,
        pricingOptions,
        pricingScope,
        hasPendingPayment,
        phoneOnPayPage,
        timeRemainingSeconds: state === "grace_period" ? graceTimeRemaining : timeRemainingSeconds,
        isInGame,
        isFreePlay,
        freePlayEndsAt,
        setIsInGame,
        createSession,
        endSession,
      }}
    >
      {children}
    </PlaySessionContext.Provider>
  );
}
